import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DialogContent } from '@/components/atoms/Dialog/Dialog';
import { TranslationQuestion } from '@/types/management/managementCategories';
import { captureAsyncError } from '@/utils/captureAsyncError';

import { EditTranslationQuestion } from './components/EditTranslationQuestion';
import { ManageQuestionPopup } from './components/ManageQuestionPopup';
import { TranslationManageQuestion } from './components/TranslationManageQuestion';

type QuestionStep = 'step1' | 'step2' | 'step2-edit';

export type QuestionPopupData = {
  text: string;
  data: TranslationQuestion[];
};

export type SetQuestionPopupData = Dispatch<SetStateAction<QuestionPopupData>>;

export type SetQuestionCategoryName = Dispatch<SetStateAction<string>>;
export type SetQuestionStep = Dispatch<SetStateAction<QuestionStep>>;

type QuestionPopupProps = {
  type: 'create' | 'edit';
  questionPopupData: QuestionPopupData;
  isLoadingTranslateQuestion?: boolean;
  isLoadingSubmitQuestion?: boolean;
  onTranslateQuestion: () => Promise<void>;
  setQuestionPopupData: SetQuestionPopupData;
  onSubmitQuestion: () => Promise<void>;
};

export const QuestionPopup: FC<QuestionPopupProps> = ({
  type,
  questionPopupData,
  isLoadingSubmitQuestion,
  isLoadingTranslateQuestion,
  onTranslateQuestion,
  setQuestionPopupData,
  onSubmitQuestion,
}) => {
  const { t } = useTranslation('components');
  const [step, setStep] = useState<QuestionStep>('step1');
  const [editingOption, setEditingOption] = useState<TranslationQuestion | null>(null);
  const [newCompanyName, setNewCompanyName] = useState('');

  const handleEditTranslation = (newValue: string) => {
    setNewCompanyName(newValue);
  };

  const handleSave = () => {
    if (!editingOption) return;

    const updatedOptions = questionPopupData.data.map(option => {
      const isEditingOption = option.languageCode === editingOption.languageCode;

      if (!isEditingOption) return option;

      return {
        ...option,
        text: newCompanyName,
      };
    });

    setQuestionPopupData(prev => ({ ...prev, data: updatedOptions }));
    setEditingOption(null);
    setStep('step2');
  };
  const handleCancel = () => {
    setEditingOption(null);
    setStep('step2');
  };

  const handleEditClick = (option: TranslationQuestion) => {
    setEditingOption(option);
    setNewCompanyName(option.text);
    setStep('step2-edit');
  };

  const handleSubmitQuestion = async () => {
    await onSubmitQuestion();
    setStep('step1');
  };

  const handleManagePopupNextStepClick = async () => {
    await onTranslateQuestion();
    setStep('step2');
  };

  const renderQuestionState = () => {
    switch (step) {
      case 'step1': {
        return (
          <ManageQuestionPopup
            type={type}
            questionPopupData={questionPopupData}
            setQuestionPopupData={setQuestionPopupData}
            onNextStepClick={handleManagePopupNextStepClick}
            isLoading={isLoadingTranslateQuestion}
          />
        );
      }
      case 'step2': {
        return (
          <TranslationManageQuestion
            translationOptions={questionPopupData.data}
            onEditClick={handleEditClick}
            setStep={setStep}
            onSubmit={() => {
              void Promise.resolve(handleSubmitQuestion()).catch(captureAsyncError);
            }}
            submitButtonLabel={
              type === 'create'
                ? t('translationManageQuestion.addQuestion')
                : t('translationManageQuestion.editQuestion')
            }
            isLoading={isLoadingSubmitQuestion}
          />
        );
      }
      case 'step2-edit': {
        return (
          <EditTranslationQuestion
            editingOption={editingOption}
            onSave={handleSave}
            onEditChange={handleEditTranslation}
            onCancel={handleCancel}
            newCompanyName={newCompanyName}
          />
        );
      }
    }
  };

  return (
    <DialogContent showCloseButton className="w-[460px]">
      {renderQuestionState()}
    </DialogContent>
  );
};
